import { GraphQL } from "@rpi/openapi-api";
import { ApiMethod } from "../components/accordions/ApiAccordion";

export const fallbackNumber = (value: any, defaultValue: number) => {
  if (typeof value !== "number" || Number.isNaN(value)) {
    return defaultValue;
  }
  return value as number;
};

export const propPlaceholder = (value: string | undefined, defaultValue: string): string => {
  if (!value || value.trim().length === 0) {
    return defaultValue;
  }
  return value;
};

export const getCurrencyFormatter = (
  currency?: GraphQL.SupportedCurrency | null,
  options: Intl.NumberFormatOptions = {}
): Intl.NumberFormat => {
  switch (currency) {
    case GraphQL.SupportedCurrency.Usd:
    default:
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 0,
        ...options,
      });
  }
};

export const formatCurrency = (
  currency: GraphQL.SupportedCurrency | undefined | null,
  value: number | undefined | null,
  options?: Intl.NumberFormatOptions
): string => {
  if (value === undefined || value === null) return "";
  return getCurrencyFormatter(currency, options).format(value);
};

const currencyFormatterCache = new Map<string, Intl.NumberFormat>();
function getCachedCurrencyFormatter(
  currency: GraphQL.SupportedCurrency = GraphQL.SupportedCurrency.Usd,
  options: Intl.NumberFormatOptions = {}
): Intl.NumberFormat {
  const key = `${currency}|${JSON.stringify(options)}`;
  const cached = currencyFormatterCache.get(key);
  if (cached) return cached;
  const newFormatter = new Intl.NumberFormat(undefined, { style: "currency", currency, ...options });
  currencyFormatterCache.set(key, newFormatter);
  return newFormatter;
}

export function formatCurrencyV2(
  currency: GraphQL.SupportedCurrency = GraphQL.SupportedCurrency.Usd,
  value = 0,
  options?: Intl.NumberFormatOptions
): string {
  return getCachedCurrencyFormatter(currency, options).format(value);
}

const CURRENCY_REGION_PREFIXES = new Map<GraphQL.SupportedCurrency, string>([
  [GraphQL.SupportedCurrency.Usd, "US"],
  [GraphQL.SupportedCurrency.Cad, "CA"],
  [GraphQL.SupportedCurrency.Gbp, "UK"],
  [GraphQL.SupportedCurrency.Eur, "EU"],
  [GraphQL.SupportedCurrency.Aud, "AU"],
]);

export function formatPaymentAmount(
  currency: GraphQL.SupportedCurrency | null | undefined,
  value: number | null | undefined
): string {
  if (value === undefined || value === null) return "";
  const code = currency ?? GraphQL.SupportedCurrency.Usd;
  const region = CURRENCY_REGION_PREFIXES.get(code) ?? code;
  return `(${region}) ${formatCurrencyV2(code, value, { maximumFractionDigits: 0 })}`;
}

export const formatAuthHeader = (apiKey: string, sharedSecret: string): string => {
  return `Basic ${btoa(`${apiKey}:${sharedSecret}`)}`;
};

export const formatCount = (count: number, item: string): string => {
  if (count === 1) return `${count} ${item}`;
  return `${count} ${item}s`;
};

export const generateJsonCurl = (props: {
  method: ApiMethod;
  url: string;
  headers?: { [key: string]: any };
  data?: any;
}): string => {
  const rows = [`curl --location --request ${props.method} '${props.url}'`];

  if (props.headers) {
    rows.push(...Object.keys(props.headers).map((key) => `--header '${key}: ${props.headers?.[key]}'`));
  }

  if (props.data) {
    rows.push("--header 'Content-Type: application/json'");
    rows.push(`--data-raw '${JSON.stringify(props.data, null, 2)}'`);
  }

  return rows.join("\\\n");
};

export const relativeTime = (hours: number, date = new Date()) => {
  return new Date(date.getTime() + hours * 60 * 60 * 1000);
};

export const getNDaysDateRange = (n: number, initialDate?: Date) => {
  const date = initialDate ? new Date(initialDate.getTime()) : new Date();

  const to = date.toISOString();
  date.setDate(date.getDate() - n);

  return {
    from: date.toISOString(),
    to,
  };
};

export function safelyParseJson<T = object>(data: string): T | null {
  try {
    return JSON.parse(data);
  } catch (_) {
    return null;
  }
}

export function optObj<T>(condition: boolean | undefined, object: T): T | undefined {
  if (condition) return object;
  return;
}

export function optArray<T>(condition: boolean | undefined, array: T[]): T[] {
  if (condition) return array;
  return [];
}

export const prettyUrl = (url: string) => {
  return url.replace(/^https*:\/\//, "").replace(/\/*$/, "");
};

export function groupBy<TProperty extends string, TData extends Record<TProperty, string>>(
  property: TProperty,
  data: TData[]
): Record<string, TData[]> {
  return data.reduce<Record<string, TData[]>>((groups, element) => {
    groups[element[property]] = groups[element[property]] ?? [];
    groups[element[property]].push(element);
    return groups;
  }, {});
}

/** Async delay. */
export function sleep(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export const HTTP_STATUS_CODE: Record<string, { message: string; description: string }> = {
  "100": {
    message: "Continue",
    description:
      "The server has received the request headers and the client should proceed to send the request body (in the case of a request for which a body needs to be sent; for example, a POST request). Sending a large request body to a server after a request has been rejected for inappropriate headers would be inefficient. To have a server check the request's headers, a client must send Expect: 100-continue as a header in its initial request and receive a 100 Continue status code in response before sending the body. The response 417 Expectation Failed indicates the request should not be continued.",
  },
  "101": {
    message: "Switching Protocols",
    description: "The requester has asked the server to switch protocols and the server has agreed to do so.",
  },
  "102": {
    message: "Processing",
    description:
      "A WebDAV request may contain many sub-requests involving file operations, requiring a long time to complete the request. This code indicates that the server has received and is processing the request, but no response is available yet. This prevents the client from timing out and assuming the request was lost.",
  },
  "103": {
    message: "Checkpoint",
    description: "A POST or PUT request that was previously aborted is to be resumed.",
  },
  "200": {
    message: "OK",
    description:
      "Standard response for successful HTTP requests. The actual response will depend on the request method used. In a GET request, the response will contain an entity corresponding to the requested resource. In a POST request, the response will contain an entity describing or containing the result of the action.",
  },
  "201": {
    message: "Created",
    description: "The request has been fulfilled, resulting in the creation of a new resource.",
  },
  "202": {
    message: "Accepted",
    description:
      "The request has been accepted for processing, but the processing has not been completed. The request might or might not be eventually acted upon, and may be disallowed when processing occurs.",
  },
  "203": {
    message: "Non-Authoritative Information",
    description:
      "The server is a transforming proxy (e.g. a Web accelerator) that received a 200 OK from its origin, but is returning a modified version of the origin's response.",
  },
  "204": {
    message: "No Content",
    description: "The server successfully processed the request and is not returning any content.",
  },
  "205": {
    message: "Reset Content",
    description:
      "The server successfully processed the request, but is not returning any content. Unlike a 204 response, this response requires that the requester reset the document view.",
  },
  "206": {
    message: "Partial Content",
    description:
      "The server is delivering only part of the resource (byte serving) due to a range header sent by the client. The range header is used by HTTP clients to enable resuming of interrupted downloads, or split a download into multiple simultaneous streams.",
  },
  "207": {
    message: "Multi-Status",
    description:
      "The message body that follows is an XML message and can contain a number of separate response codes, depending on how many sub-requests were made.",
  },
  "208": {
    message: "Already Reported",
    description:
      "The members of a DAV binding have already been enumerated in a previous reply to this request, and are not being included again.",
  },
  "300": {
    message: "Multiple Choices",
    description:
      "Indicates multiple options for the resource from which the client may choose (via agent-driven content negotiation). For example, this code could be used to present multiple video format options, to list files with different filename extensions, or to suggest word-sense disambiguation.",
  },
  "301": {
    message: "Moved Permanently",
    description: "This and all future requests should be directed to the given URI.",
  },
  "302": {
    message: "Found",
    description:
      "This is an example of industry practice contradicting the standard. The HTTP/1.0 specification (RFC 1945) required the client to perform a temporary redirect (the original describing phrase was 'Moved Temporarily'), but popular browsers implemented 302 with the functionality of a 303 See Other. Therefore, HTTP/1.1 added status codes 303 and 307 to distinguish between the two behaviours. However, some Web applications and frameworks use the 302 status code as if it were the 303.",
  },
  "303": {
    message: "See Other",
    description:
      "The response to the request can be found under another URI using a GET method. When received in response to a POST (or PUT/DELETE), the client should presume that the server has received the data and should issue a redirect with a separate GET message.",
  },
  "304": {
    message: "Not Modified",
    description:
      "Indicates that the resource has not been modified since the version specified by the request headers If-Modified-Since or If-None-Match. In such case, there is no need to retransmit the resource since the client still has a previously-downloaded copy.",
  },
  "305": {
    message: "Use Proxy",
    description:
      "The requested resource is available only through a proxy, the address for which is provided in the response. Many HTTP clients (such as Mozilla and Internet Explorer) do not correctly handle responses with this status code, primarily for security reasons.",
  },
  "306": {
    message: "Switch Proxy",
    description: "No longer used. Originally meant 'Subsequent requests should use the specified proxy.'",
  },
  "307": {
    message: "Temporary Redirect",
    description:
      "In this case, the request should be repeated with another URI; however, future requests should still use the original URI. In contrast to how 302 was historically implemented, the request method is not allowed to be changed when reissuing the original request. For example, a POST request should be repeated using another POST request.",
  },
  "308": {
    message: "Permanent Redirect",
    description:
      "The request and all future requests should be repeated using another URI. 307 and 308 parallel the behaviors of 302 and 301, but do not allow the HTTP method to change. So, for example, submitting a form to a permanently redirected resource may continue smoothly.",
  },
  "400": {
    message: "Bad Request",
    description:
      "The server cannot or will not process the request due to an apparent client error (e.g., malformed request syntax, too large size, invalid request message framing, or deceptive request routing).",
  },
  "401": {
    message: "Unauthorized",
    description:
      "Similar to 403 Forbidden, but specifically for use when authentication is required and has failed or has not yet been provided. The response must include a WWW-Authenticate header field containing a challenge applicable to the requested resource. See Basic access authentication and Digest access authentication.[33] 401 semantically means 'unauthenticated' the user does not have the necessary credentials. Note: Some sites issue HTTP 401 when an IP address is banned from the website (usually the website domain) and that specific address is refused permission to access a website.",
  },
  "402": {
    message: "Payment Required",
    description:
      "Reserved for future use. The original intention was that this code might be used as part of some form of digital cash or micropayment scheme, but that has not happened, and this code is not usually used. Google Developers API uses this status if a particular developer has exceeded the daily limit on requests.",
  },
  "403": {
    message: "Forbidden",
    description:
      "The request was a valid request, but the server is refusing to respond to it. The user might be logged in but does not have the necessary permissions for the resource.",
  },
  "404": {
    message: "Not Found",
    description:
      "The requested resource could not be found but may be available in the future. Subsequent requests by the client are permissible.",
  },
  "405": {
    message: "Method Not Allowed",
    description:
      "A request method is not supported for the requested resource; for example, a GET request on a form which requires data to be presented via POST, or a PUT request on a read-only resource.",
  },
  "406": {
    message: "Not Acceptable",
    description:
      "The requested resource is capable of generating only content not acceptable according to the Accept headers sent in the request.",
  },
  "407": {
    message: "Proxy Authentication Required",
    description: "The client must first authenticate itself with the proxy.",
  },
  "408": {
    message: "Request Time-out",
    description:
      "The server timed out waiting for the request. According to HTTP specifications: 'The client did not produce a request within the time that the server was prepared to wait. The client MAY repeat the request without modifications at any later time.'",
  },
  "409": {
    message: "Conflict",
    description:
      "Indicates that the request could not be processed because of conflict in the request, such as an edit conflict between multiple simultaneous updates.",
  },
  "410": {
    message: "Gone",
    description:
      "Indicates that the resource requested is no longer available and will not be available again. This should be used when a resource has been intentionally removed and the resource should be purged. Upon receiving a 410 status code, the client should not request the resource in the future. Clients such as search engines should remove the resource from their indices. Most use cases do not require clients and search engines to purge the resource, and a '404 Not Found' may be used instead.",
  },
  "411": {
    message: "Length Required",
    description: "The request did not specify the length of its content, which is required by the requested resource.",
  },
  "412": {
    message: "Precondition Failed",
    description: "The server does not meet one of the preconditions that the requester put on the request.",
  },
  "413": {
    message: "Request Entity Too Large",
    description:
      "The request is larger than the server is willing or able to process. Previously called 'Request Entity Too Large'.",
  },
  "414": {
    message: "Request-URI Too Long",
    description:
      "The URI provided was too long for the server to process. Often the result of too much data being encoded as a query-string of a GET request, in which case it should be converted to a POST request. Called 'Request-URI Too Long' previously.",
  },
  "415": {
    message: "Unsupported Media Type",
    description:
      "The request entity has a media type which the server or resource does not support. For example, the client uploads an image as image/svg+xml, but the server requires that images use a different format.",
  },
  "416": {
    message: "Requested Range Not Satisfiable",
    description:
      "The client has asked for a portion of the file (byte serving), but the server cannot supply that portion. For example, if the client asked for a part of the file that lies beyond the end of the file.[46] Called 'Requested Range Not Satisfiable' previously.",
  },
  "417": {
    message: "Expectation Failed",
    description: "The server cannot meet the requirements of the Expect request-header field.",
  },
  "418": {
    message: "I'm a teapot",
    description:
      "This code was defined in 1998 as one of the traditional IETF April Fools' jokes, in RFC 2324, Hyper Text Coffee Pot Control Protocol, and is not expected to be implemented by actual HTTP servers. The RFC specifies this code should be returned by teapots requested to brew coffee.[49] This HTTP status is used as an Easter egg in some websites, including Google.com.",
  },
  "421": {
    message: "Unprocessable Entity",
    description:
      "The request was directed at a server that is not able to produce a response (for example because a connection reuse).",
  },
  "422": {
    message: "Misdirected Request",
    description: "The request was well-formed but was unable to be followed due to semantic errors.",
  },
  "423": {
    message: "Locked",
    description: "The resource that is being accessed is locked.",
  },
  "424": {
    message: "Failed Dependency",
    description: "The request failed due to failure of a previous request (e.g., a PROPPATCH).",
  },
  "426": {
    message: "Upgrade Required",
    description: "The client should switch to a different protocol such as TLS/1.0, given in the Upgrade header field.",
  },
  "428": {
    message: "Precondition Required",
    description:
      "The origin server requires the request to be conditional. Intended to prevent the 'lost update' problem, where a client GETs a resource's state, modifies it, and PUTs it back to the server, when meanwhile a third party has modified the state on the server, leading to a conflict.",
  },
  "429": {
    message: "Too Many Requests",
    description:
      "The user has sent too many requests in a given amount of time. Intended for use with rate-limiting schemes.",
  },
  "431": {
    message: "Request Header Fileds Too Large",
    description:
      "The server is unwilling to process the request because either an individual header field, or all the header fields collectively, are too large.",
  },
  "451": {
    message: "Unavailable For Legal Reasons",
    description:
      "A server operator has received a legal demand to deny access to a resource or to a set of resources that includes the requested resource. The code 451 was chosen as a reference to the novel Fahrenheit 451.",
  },
  "500": {
    message: "Internal Server Error",
    description:
      "A generic error message, given when an unexpected condition was encountered and no more specific message is suitable.",
  },
  "501": {
    message: "Not Implemented",
    description:
      "The server either does not recognize the request method, or it lacks the ability to fulfill the request. Usually this implies future availability (e.g., a new feature of a web-service API).",
  },
  "502": {
    message: "Bad Gateway",
    description:
      "The server was acting as a gateway or proxy and received an invalid response from the upstream server.",
  },
  "503": {
    message: "Service Unavailable",
    description:
      "The server is currently unavailable (because it is overloaded or down for maintenance). Generally, this is a temporary state.",
  },
  "504": {
    message: "Gateway Timeout",
    description:
      "The server was acting as a gateway or proxy and did not receive a timely response from the upstream server.",
  },
  "505": {
    message: "HTTP Version Not Supported",
    description: "The server does not support the HTTP protocol version used in the request.",
  },
  "506": {
    message: "Variant Also Negotiates",
    description: "Transparent content negotiation for the request results in a circular reference.",
  },
  "507": {
    message: "Insufficient Storage",
    description: "The server is unable to store the representation needed to complete the request.",
  },
  "508": {
    message: "Loop Detected",
    description:
      "The server detected an infinite loop while processing the request (sent in lieu of 208 Already Reported).",
  },
  "509": {
    message: "Bandwidth Limit Exceeded",
    description: "Bandwidth Limit Exceeded. This status code, despite being used by many servers, is not official.",
  },
  "510": {
    message: "Not Extended",
    description: "Further extensions to the request are required for the server to fulfill it.",
  },
  "511": {
    message: "Network Authentication Required",
    description:
      "The client needs to authenticate to gain network access. Intended for use by intercepting proxies used to control access to the network (e.g., 'captive portals' used to require agreement to Terms of Service before granting full Internet access via a Wi-Fi hotspot).",
  },
  "1xx": {
    message: "Informational",
    description:
      "Request received, continuing process. This class of status code indicates a provisional response, consisting only of the Status-Line and optional headers, and is terminated by an empty line. Since HTTP/1.0 did not define any 1xx status codes, servers must not send a 1xx response to an HTTP/1.0 client except under experimental conditions.",
  },
  "2xx": {
    message: "Success",
    description:
      "This class of status codes indicates the action requested by the client was received, understood, accepted, and processed successfully.",
  },
  "3xx": {
    message: "Redirection",
    description:
      "This class of status code indicates the client must take additional action to complete the request. Many of these status codes are used in URL redirection. A user agent may carry out the additional action with no user interaction only if the method used in the second request is GET or HEAD. A user agent may automatically redirect a request. A user agent should detect and intervene to prevent cyclical redirects.",
  },
  "4xx": {
    message: "Client Error",
    description:
      "The 4xx class of status code is intended for situations in which the client seems to have erred. Except when responding to a HEAD request, the server should include an entity containing an explanation of the error situation, and whether it is a temporary or permanent condition. These status codes are applicable to any request method. User agents should display any included entity to the user.",
  },
  "5xx": {
    message: "Server Error",
    description:
      "The server failed to fulfill an apparently valid request. Response status codes beginning with the digit 5 indicate cases in which the server is aware that it has encountered an error or is otherwise incapable of performing the request. Except when responding to a HEAD request, the server should include an entity containing an explanation of the error situation, and indicate whether it is a temporary or permanent condition. Likewise, user agents should display any included entity to the user. These response codes are applicable to any request method.",
  },
};
